/**
 * Gravity Well Alpine.js Component
 * Interactive canvas with particles that orbit and slingshot around the cursor.
 * Uses Verlet integration for smooth, stable physics.
 */

import { observeFooterReveal } from './utils/footer-reveal.js';

export default () => ({
    // Configuration
    PARTICLE_COUNT: 240,
    GRAVITY_STRENGTH: 800,
    CURSOR_RADIUS: 250,
    TRAIL_LENGTH: 8,
    PARTICLE_RADIUS: 2,
    MAX_SPEED: 8,
    FRICTION: 0.98,
    DRIFT_SPEED: 0.4,
    SHOCKWAVE_STRENGTH: 12,

    // Brand colors
    COLOR_PRIMARY:   { h: 94,  s: 100, l: 42 },
    COLOR_SECONDARY: { h: 235, s: 100, l: 72 },

    // State
    canvas: null,
    ctx: null,
    width: 0,
    height: 0,
    dpr: 1,
    particles: [],
    mouse: { x: -9999, y: -9999, prevX: -9999, prevY: -9999, speed: 0, active: false },
    shockwave: null,  // { x, y, radius, maxRadius, strength }
    animationId: null,
    resizeHandler: null,
    prefersReducedMotion: false,

    init() {
        if (!window.matchMedia('(min-width: 768px)').matches) return;

        this.canvas = this.$refs.canvas;
        if (!this.canvas) return;

        this.ctx = this.canvas.getContext('2d');
        if (!this.ctx) return;

        this.dpr = window.devicePixelRatio || 1;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.resizeHandler = this.resize.bind(this);
        this._onMouseMove = (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const m = this.mouse;
            m.prevX = m.x;
            m.prevY = m.y;
            m.x = e.clientX - rect.left;
            m.y = e.clientY - rect.top;
            const dx = m.x - m.prevX;
            const dy = m.y - m.prevY;
            m.speed = Math.sqrt(dx * dx + dy * dy);
            m.active = true;
        };
        this._onMouseLeave = () => { this.mouse.active = false; this.mouse.speed = 0; };
        this._onClick = (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.shockwave = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
                radius: 0,
                maxRadius: 350,
                strength: this.SHOCKWAVE_STRENGTH,
            };
        };

        window.addEventListener('resize', this.resizeHandler);
        this.canvas.addEventListener('mousemove', this._onMouseMove);
        this.canvas.addEventListener('mouseleave', this._onMouseLeave);
        this.canvas.addEventListener('click', this._onClick);

        this.resize();
        this.$el.classList.remove('opacity-0');

        if (this.prefersReducedMotion) {
            this.draw();
        } else {
            this.stopObservingFooter = observeFooterReveal((revealed) => revealed ? this.start() : this.stop());
        }
    },

    start() {
        if (!this.animationId) this.loop();
    },

    stop() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    },

    destroy() {
        this.stopObservingFooter?.();
        this.stop();
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        if (this.canvas) {
            this.canvas.removeEventListener('mousemove', this._onMouseMove);
            this.canvas.removeEventListener('mouseleave', this._onMouseLeave);
            this.canvas.removeEventListener('click', this._onClick);
        }
    },

    resize() {
        const parent = this.canvas.parentElement;
        this.width = parent.clientWidth;
        this.height = parent.clientHeight;
        this.canvas.width = this.width * this.dpr;
        this.canvas.height = this.height * this.dpr;
        this.canvas.style.width = this.width + 'px';
        this.canvas.style.height = this.height + 'px';
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        this.initParticles();
    },

    initParticles() {
        const count = this.PARTICLE_COUNT;
        const w = this.width;
        const h = this.height;
        this.particles = [];

        for (let i = 0; i < count; i++) {
            const x = Math.random() * w;
            const y = Math.random() * h;
            const angle = Math.random() * Math.PI * 2;
            const speed = this.DRIFT_SPEED * (0.3 + Math.random() * 0.7);

            // Color assignment: blend based on index for variety
            const t = i / (count - 1);

            this.particles.push({
                x,
                y,
                prevX: x - Math.cos(angle) * speed,
                prevY: y - Math.sin(angle) * speed,
                trail: [],
                colorT: t,
                size: this.PARTICLE_RADIUS * (0.6 + Math.random() * 0.8),
            });
        }
    },

    update() {
        // Cache to avoid Alpine proxy overhead
        const particles = this.particles;
        const count = particles.length;
        const mouse = this.mouse;
        const baseGravity = this.GRAVITY_STRENGTH;
        const cursorRadius = this.CURSOR_RADIUS;
        const maxSpeed = this.MAX_SPEED;
        const friction = this.FRICTION;
        const trailLen = this.TRAIL_LENGTH;
        const w = this.width;
        const h = this.height;
        const shockwave = this.shockwave;

        // Velocity-scaled gravity: faster cursor = stronger pull (1x to 3x)
        const mouseSpeedFactor = mouse.active ? 1 + Math.min(2, mouse.speed / 15) : 1;
        const gravity = baseGravity * mouseSpeedFactor;

        // Advance shockwave
        if (shockwave) {
            shockwave.radius += 12;
            shockwave.strength *= 0.96;
            if (shockwave.radius > shockwave.maxRadius) {
                this.shockwave = null;
            }
        }

        for (let i = 0; i < count; i++) {
            const p = particles[i];

            // Verlet integration: compute velocity from positions
            let vx = (p.x - p.prevX) * friction;
            let vy = (p.y - p.prevY) * friction;

            // Gravity toward cursor
            if (mouse.active) {
                const dx = mouse.x - p.x;
                const dy = mouse.y - p.y;
                const distSq = dx * dx + dy * dy;
                const minDist = 30; // prevent singularity at cursor center

                if (distSq < cursorRadius * cursorRadius && distSq > minDist * minDist) {
                    const dist = Math.sqrt(distSq);
                    // Inverse-square gravity, capped
                    const force = gravity / distSq;
                    const fx = (dx / dist) * force;
                    const fy = (dy / dist) * force;
                    vx += fx;
                    vy += fy;
                } else if (distSq <= minDist * minDist && distSq > 1) {
                    // Very close — tangential push to maintain orbit, not collapse
                    const dist = Math.sqrt(distSq);
                    const tx = -dy / dist;
                    const ty = dx / dist;
                    vx += tx * 0.5;
                    vy += ty * 0.5;
                    // Gentle outward nudge
                    vx += (p.x - mouse.x) / dist * 0.3;
                    vy += (p.y - mouse.y) / dist * 0.3;
                }
            }

            // Shockwave blast
            if (shockwave) {
                const sdx = p.x - shockwave.x;
                const sdy = p.y - shockwave.y;
                const sDist = Math.sqrt(sdx * sdx + sdy * sdy);
                // Affect particles near the expanding ring (within a band)
                const ringDist = Math.abs(sDist - shockwave.radius);
                if (ringDist < 40 && sDist > 1) {
                    const influence = (1 - ringDist / 40) * shockwave.strength;
                    vx += (sdx / sDist) * influence;
                    vy += (sdy / sDist) * influence;
                }
            }

            // Soft boundary — pull particles back toward canvas when outside
            const margin = 50;
            const pullStrength = 0.08;
            if (p.x < -margin) vx += (-margin - p.x) * pullStrength + 0.5;
            else if (p.x > w + margin) vx += (w + margin - p.x) * pullStrength - 0.5;
            if (p.y < -margin) vy += (-margin - p.y) * pullStrength + 0.5;
            else if (p.y > h + margin) vy += (h + margin - p.y) * pullStrength - 0.5;

            // Gentle nudge when near edges (keeps particles drifting inward)
            if (p.x < 0) vx += 0.1;
            else if (p.x > w) vx -= 0.1;
            if (p.y < 0) vy += 0.1;
            else if (p.y > h) vy -= 0.1;

            // Clamp speed
            const speed = Math.sqrt(vx * vx + vy * vy);
            if (speed > maxSpeed) {
                const scale = maxSpeed / speed;
                vx *= scale;
                vy *= scale;
            }

            // Store trail
            p.trail.push(p.x, p.y);
            if (p.trail.length > trailLen * 2) {
                p.trail.splice(0, 2);
            }

            // Update positions (Verlet)
            p.prevX = p.x;
            p.prevY = p.y;
            p.x += vx;
            p.y += vy;
        }
    },

    draw() {
        const ctx = this.ctx;
        const w = this.width;
        const h = this.height;
        const particles = this.particles;
        const count = particles.length;
        const cP = this.COLOR_PRIMARY;
        const cS = this.COLOR_SECONDARY;

        ctx.clearRect(0, 0, w, h);

        for (let i = 0; i < count; i++) {
            const p = particles[i];
            const t = p.colorT;

            // Interpolate color
            const ch = cS.h + (cP.h - cS.h) * t;
            const cs = cS.s + (cP.s - cS.s) * t;
            const cl = cS.l + (cP.l - cS.l) * t;

            // Compute current speed for brightness boost
            const vx = p.x - p.prevX;
            const vy = p.y - p.prevY;
            const speed = Math.sqrt(vx * vx + vy * vy);
            const speedFactor = Math.min(1, speed / 5);

            // Draw tapered trail as segments that widen toward the head
            const trail = p.trail;
            const trailPairs = trail.length / 2;
            if (trailPairs > 1) {
                const baseAlpha = 0.15 + speedFactor * 0.25;
                for (let j = 0; j < trailPairs - 1; j++) {
                    const segT = (j + 1) / trailPairs;  // 0 at tail, 1 at head
                    const nextT = (j + 2) / trailPairs;
                    ctx.beginPath();
                    ctx.moveTo(trail[j * 2], trail[j * 2 + 1]);
                    ctx.lineTo(trail[j * 2 + 2], trail[j * 2 + 3]);
                    ctx.strokeStyle = `hsla(${ch}, ${cs}%, ${cl}%, ${baseAlpha * segT})`;
                    ctx.lineWidth = p.size * 2 * nextT;
                    ctx.lineCap = 'round';
                    ctx.stroke();
                }
                // Final segment to current position
                ctx.beginPath();
                ctx.moveTo(trail[trail.length - 2], trail[trail.length - 1]);
                ctx.lineTo(p.x, p.y);
                ctx.strokeStyle = `hsla(${ch}, ${cs}%, ${cl}%, ${baseAlpha})`;
                ctx.lineWidth = p.size * 2;
                ctx.lineCap = 'round';
                ctx.stroke();
            } else {
                // No trail yet — just draw a dot
                const alpha = 0.5 + speedFactor * 0.5;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `hsla(${ch}, ${cs}%, ${cl}%, ${alpha})`;
                ctx.fill();
            }
        }

        // Draw shockwave ring
        const shockwave = this.shockwave;
        if (shockwave) {
            const progress = shockwave.radius / shockwave.maxRadius;
            const alpha = (1 - progress) * 0.4;
            ctx.beginPath();
            ctx.arc(shockwave.x, shockwave.y, shockwave.radius, 0, Math.PI * 2);
            ctx.strokeStyle = `hsla(${cS.h}, ${cS.s}%, ${cS.l}%, ${alpha})`;
            ctx.lineWidth = 2 * (1 - progress);
            ctx.stroke();
        }

        // Draw black hole at cursor position when active
        if (this.mouse.active) {
            const mx = this.mouse.x;
            const my = this.mouse.y;

            // Dark core with soft falloff
            const grd = ctx.createRadialGradient(mx, my, 0, mx, my, 120);
            grd.addColorStop(0, 'rgba(0, 0, 0, 0.6)');
            grd.addColorStop(0.15, 'rgba(0, 0, 0, 0.35)');
            grd.addColorStop(0.5, 'rgba(0, 0, 0, 0.1)');
            grd.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.beginPath();
            ctx.arc(mx, my, 120, 0, Math.PI * 2);
            ctx.fillStyle = grd;
            ctx.fill();
        }
    },

    loop() {
        this.update();
        this.draw();
        this.animationId = requestAnimationFrame(() => this.loop());
    },
});
